<template>
  <div class="py-6 options-container">
    <h2 class="text-text-primary mb-6 options-title">
      {{ $t('project.checklist.dialogs.SmartImportOptions.title') }}
    </h2>

    <div class="d-flex options-list">
      <div
        v-for="(option, index) in options"
        :key="index"
        class="option-card pa-6 d-flex align-center flex-1-1-0"
        :class="{ 'selected': option.value === modelValue }"
        @click="selectOption(option.value)"
        :data-locator="option.locator"
      >
        <div class="d-flex align-center justify-center mr-4 option-icon" :class="option.iconBgClass">
          <app-material-icon
            :name="option.icon"
            :color="option.iconColor"
          />
        </div>
        <div class="flex-grow-1">
          <p class="option-title mb-0">
            {{ $t(option.titleKey) }}
          </p>
          <p class="option-subtitle text-text-secondary mb-0">
            {{ $t(option.descriptionKey) }}
          </p>
        </div>
        <div v-if="option.value === modelValue" class="d-flex align-center justify-center">
          <app-material-icon
            name="check_circle"
            size="sm"
            color="icon-info-bold"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

type ImportMode = 'replace' | 'append'

export default defineComponent({
  name: 'SmartImportOptions',

  props: {
    modelValue: {
      type: String as () => ImportMode,
      default: 'replace',
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      options: [
        {
          value: 'replace' as ImportMode,
          icon: 'cached',
          iconColor: 'icon-lime',
          iconBgClass: 'option-icon-replace',
          titleKey: 'project.checklist.dialogs.SmartImportOptions.replaceTitle',
          descriptionKey: 'project.checklist.dialogs.SmartImportOptions.replaceDescription',
          locator: 'replace-option',
        },
        {
          value: 'append' as ImportMode,
          icon: 'add',
          iconColor: 'icon-orange',
          iconBgClass: 'option-icon-append',
          titleKey: 'project.checklist.dialogs.SmartImportOptions.appendTitle',
          descriptionKey: 'project.checklist.dialogs.SmartImportOptions.appendDescription',
          locator: 'append-option',
        },
      ],
    }
  },
  methods: {
    selectOption(mode: ImportMode) {
      this.$emit('update:modelValue', mode)
    },
  },
})
</script>

<style lang="scss" scoped>
.options-container {
  min-height: 100%;
  border-bottom: 1px solid rgb(var(--v-theme-border-primary));
  border-top: 1px solid rgb(var(--v-theme-border-primary));
}

.options-title {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.options-list {
  gap: 16px;
  flex-direction: row;
}

.option-icon {
  min-width: 40px;
  height: 40px;
  border-radius: 8px;

  &.option-icon-replace {
    background-color: rgb(var(--v-theme-surface-lime-subtle));
  }

  &.option-icon-append {
    background-color: rgb(var(--v-theme-surface-orange-subtle));
  }
}

.option-title {
  color: rgb(var(--v-theme-text-primary));
  font-weight: 600;
}
.option-title, .option-subtitle {
  line-height: 20px;
}

.option-card {
  border: 2px solid rgb(var(--v-theme-border-primary));
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &.selected {
    border-color: rgb(var(--v-theme-border-info-bold));
    background-color: rgb(var(--v-theme-surface-info)) !important;
  }
}

</style>
